import {HttpEvent, HttpParams} from "@angular/common/http";
import {Observable} from "rxjs";
import {CasaNativeApiV3} from "../casa-native-api";
import {
  CountryDefinition,
  V3Address,
  V3ContactEntry,
  V3ContactEntryDetails,
  V3Person,
  V3PersonalSensitiveDetails,
  V3PersonDetails,
  V3Settings,
} from "../../../model/v3/people";
import {V3FileItem} from "src/app/auth_profile/model/v3/file";
import {SchoolStudentStatus, V3School, V3SchoolTeacher, V3Student, V3Teacher} from "src/app/auth_profile/model/v3/schools";
import {Page, Pageable} from "src/app/utils/pageable";
import {HttpMethod} from "../../../../constraints";
import {appendCollection, appendIfDefined, createPageableParams, serializeDateInput} from "../v3/rest-v3-utils";

export abstract class CasaMobileBaseService implements CasaNativeApiV3 {
  protected abstract request<T>(method: HttpMethod, path: string, body?: any, params?: HttpParams): Observable<T>;

  uploadFile(file: File): Observable<HttpEvent<any>> {
    throw new Error("Method not implemented.");
  }

  getPerson(personId: number): Observable<V3Person> {
    return this.request<V3Person>(HttpMethod.GET, `/v1/people/${personId}`);
  }

  updatePerson(personId: number, details: V3PersonDetails): Observable<V3Person> {
    return this.request<V3Person>(HttpMethod.PUT, `/v1/people/${personId}`, details);
  }

  deletePerson(personId: number): Observable<void> {
    return this.request<void>(HttpMethod.DELETE, `/v1/people/${personId}`);
  }

  listContacts(personId: number): Observable<V3ContactEntry[]> {
    return this.request<V3ContactEntry[]>(HttpMethod.GET, `/v1/people/${personId}/contacts`);
  }

  createContact(personId: number, contact: V3ContactEntryDetails): Observable<void> {
    return this.request<void>(HttpMethod.POST, `/v1/people/${personId}/contacts`, contact);
  }

  updateContact(personId: number, contactId: number, contact: V3ContactEntryDetails): Observable<void> {
    return this.request<void>(HttpMethod.PUT, `/v1/people/${personId}/contacts/${contactId}`, contact);
  }

  getAddress(personId: number): Observable<V3Address> {
    return this.request<V3Address>(HttpMethod.GET, `/v1/people/${personId}/address`);
  }

  updateAddress(personId: number, address: V3Address): Observable<void> {
    return this.request<void>(HttpMethod.PUT, `/v1/people/${personId}/address`, address);
  }

  getSensitive(personId: number): Observable<V3PersonalSensitiveDetails> {
    return this.request<V3PersonalSensitiveDetails>(HttpMethod.GET, `/v1/people/${personId}/sensitive`);
  }

  updateSensitive(personId: number, sensitive: V3PersonalSensitiveDetails): Observable<void> {
    return this.request<void>(HttpMethod.PUT, `/v1/people/${personId}/sensitive`, sensitive);
  }

  getPhoto(personId: number): Observable<V3FileItem> {
    return this.request<V3FileItem>(HttpMethod.GET, `/v1/people/${personId}/photo`);
  }

  updatePhoto(personId: number, file: V3FileItem): Observable<void> {
    return this.request<void>(HttpMethod.PUT, `/v1/people/${personId}/photo`, file);
  }

  getSettings(personId: number): Observable<V3Settings> {
    return this.request<V3Settings>(HttpMethod.GET, `/v1/people/${personId}/settings`);
  }

  updateSettings(personId: number, settings: V3Settings): Observable<void> {
    return this.request<void>(HttpMethod.PUT, `/v1/people/${personId}/settings`, settings);
  }

  listCountries(): Observable<CountryDefinition[]> {
    return this.request<CountryDefinition[]>(HttpMethod.GET, `/public/countries`);
  }

  getSchool(schoolId: number): Observable<V3School> {
    return this.request<V3School>(HttpMethod.GET, `/v1/schools/${schoolId}`);
  }

  getStudents(
    schoolId: number,
    term?: string | null,
    status?: SchoolStudentStatus[] | null,
    expiry?: Date | string | null,
    pageable?: Pageable,
  ): Observable<Page<V3Student>> {
    let params = createPageableParams(pageable);
    params = appendIfDefined(params, "term", term);
    params = appendCollection(params, "status", status ?? undefined);
    params = appendIfDefined(params, "expiry", serializeDateInput(expiry));
    return this.request<Page<V3Student>>(HttpMethod.GET, `/v1/schools/${schoolId}/students`, null, params);
  }

  getStudent(schoolId: number, studentId: number): Observable<V3Student> {
    return this.request<V3Student>(HttpMethod.GET, `/v1/schools/${schoolId}/students/${studentId}`);
  }

  getTeacher(schoolId: number, teacherId: number): Observable<V3Teacher> {
    return this.request<V3Teacher>(HttpMethod.GET, `/v1/schools/${schoolId}/teachers/${teacherId}`);
  }

  isImplemented(): boolean {
    return true;
  }

  supportsFileSending(): boolean {
    return false;
  }

  isSupportsProfileEdit(): Boolean {
    return false;
  }

  findTeachers(schoolId: number, term?: string | null, productCode?: string | null, pageable?: Pageable | null): Observable<Page<V3Teacher>> {
    throw new Error("Method not implemented.");
  }

  getTeachers(
    schoolId?: number | null,
    personId?: number | null,
    email?: string | null,
    teacherIds?: number[] | null,
    pageable?: Pageable | null
  ): Observable<Page<V3SchoolTeacher>> {
    throw new Error("Method not implemented.");
  }
}
